'use client'

import React, { useState, useEffect } from 'react'
import { StrategyNote } from '@/types'
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  FileText,
  Clock,
  AlertCircle
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { getStrategyNotes, recordStrategyNote, updateStrategyNote, deleteStrategyNote } from '../lib/api'
import Toast from './Toast'
import ConfirmDialog from './ConfirmDialog'

interface ResearchNotesProps {
  strategyId: string
  latestNote?: StrategyNote
}

const ResearchNotes: React.FC<ResearchNotesProps> = ({ strategyId }) => {
  const [notes, setNotes] = useState<StrategyNote[]>([])
  const [editingNote, setEditingNote] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingNote, setDeletingNote] = useState<StrategyNote | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_version_update: false,
    impact_scope: ''
  })

  useEffect(() => {
    fetchNotes()
  }, [strategyId])

  const fetchNotes = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getStrategyNotes(strategyId)
      setNotes(data)
    } catch (error) {
      console.error('获取笔记失败:', error)
      setError('获取笔记失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const noteData = {
        strategy_id: strategyId,
        title: formData.title,
        content: formData.content,
        is_version_update: formData.is_version_update,
        impact_scope: formData.impact_scope
      }
      
      if (editingNote) {
        // 更新现有笔记
        await updateStrategyNote(editingNote, noteData)
        setToast({ type: 'success', message: '笔记更新成功' })
      } else {
        // 创建新笔记
        await recordStrategyNote(noteData)
        setToast({ type: 'success', message: '笔记保存成功' })
      }
      
      // 重新获取笔记列表
      await fetchNotes()
      
      setEditingNote(null)
      setShowAddForm(false)
      setFormData({
        title: '',
        content: '',
        is_version_update: false,
        impact_scope: ''
      })
    } catch (error) {
      console.error('保存笔记失败:', error)
      setError('保存笔记失败，请稍后重试')
      setToast({ type: 'error', message: '保存笔记失败' })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (noteId: string) => {
    const note = notes.find(n => n.id === noteId)
    if (note) {
      setFormData({
        title: note.title,
        content: note.content,
        is_version_update: note.is_version_update,
        impact_scope: note.impact_scope || ''
      })
      setEditingNote(noteId)
    }
  }

  const handleDelete = async () => {
    if (!deletingNote) return

    try {
      setLoading(true)
      setError(null)
      
      await deleteStrategyNote(deletingNote.id)
      await fetchNotes()
      setDeletingNote(null)
      setToast({ type: 'success', message: '笔记删除成功' })
    } catch (error) {
      console.error('删除笔记失败:', error)
      setError('删除笔记失败，请稍后重试')
      setToast({ type: 'error', message: '删除笔记失败' })
    } finally {
      setLoading(false)
    }
  }

  const getImpactScopeColor = (scope: string) => {
    switch (scope) {
      case '参数调整':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      case '风控调整':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      case '策略优化':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case '市场分析':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">研究笔记</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center whitespace-nowrap"
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-2" />
          添加笔记
        </button>
      </div>

      {/* 错误显示 */}
      {error && (
        <div className="card bg-red-500/10 border border-red-500/30">
          <div className="flex items-center text-red-400">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* 加载状态 */}
      {loading && (
        <div className="card">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-400"></div>
            <span className="ml-3 text-white/70">加载中...</span>
          </div>
        </div>
      )}

      {/* 笔记列表 */}
      {!loading && (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{note.title}</h3>
                    {note.is_version_update && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-500/20 text-violet-400 border border-violet-500/30">
                        版本更新
                      </span>
                    )}
                    {note.impact_scope && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactScopeColor(note.impact_scope)}`}>
                        {note.impact_scope}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center text-sm text-white/60 mb-3">
                    <Clock className="w-4 h-4 mr-1" />
                    {new Date(note.created_at).toLocaleString('zh-CN')}
                  </div>
                  <div className="prose prose-sm max-w-none prose-invert">
                    <ReactMarkdown>{note.content}</ReactMarkdown>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(note.id)}
                    className="text-violet-400 hover:text-violet-300"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeletingNote(note)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 添加/编辑表单 */}
      {(showAddForm || editingNote) && (
        <div className="card">
          <NoteEditForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={() => {
              setEditingNote(null)
              setShowAddForm(false)
              setFormData({
                title: '',
                content: '',
                is_version_update: false,
                impact_scope: ''
              })
            }}
            loading={loading}
          />
        </div>
      )}

      {!loading && notes.length === 0 && !showAddForm && (
        <div className="empty-state">
          <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">暂无研究笔记</h3>
          <p className="text-white/50 mb-6">开始记录您的研究发现</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            添加第一条笔记
          </button>
        </div>
      )}

      {/* 删除确认对话框 */}
      {deletingNote && (
        <ConfirmDialog
          isOpen={!!deletingNote}
          onCancel={() => setDeletingNote(null)}
          onConfirm={handleDelete}
          title="确认删除"
          message={`确定要删除笔记 "${deletingNote.title}" 吗？此操作不可逆。`}
          confirmText="删除"
          cancelText="取消"
          type="danger"
        />
      )}

      {/* 提示信息 */}
      {toast && (
        <Toast
          message={toast.message}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

interface NoteEditFormProps {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  loading?: boolean
}

const NoteEditForm: React.FC<NoteEditFormProps> = ({
  formData,
  setFormData,
  onSave,
  onCancel,
  loading = false
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">笔记信息</h3>
      
      <div>
        <label className="block text-sm font-medium text-white mb-2">标题</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full"
          placeholder="笔记标题..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-white mb-2">内容</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          className="w-full"
          rows={8}
          placeholder="使用 Markdown 格式编写笔记内容..."
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">影响范围</label>
          <select
            value={formData.impact_scope}
            onChange={(e) => setFormData({ ...formData, impact_scope: e.target.value })}
            className="w-full"
          >
            <option value="">选择影响范围</option>
            <option value="参数调整">参数调整</option>
            <option value="风控调整">风控调整</option>
            <option value="策略优化">策略优化</option>
            <option value="市场分析">市场分析</option>
            <option value="其他">其他</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-white mb-2">版本更新</label>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="version_update"
              checked={formData.is_version_update}
              onChange={(e) => setFormData({ ...formData, is_version_update: e.target.checked })}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-white/20 rounded"
            />
            <label htmlFor="version_update" className="ml-2 block text-sm text-white/70">
              标记为版本更新
            </label>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end space-x-3">
        <button onClick={onCancel} className="btn-secondary flex items-center whitespace-nowrap" disabled={loading}>
          <X className="w-4 h-4 mr-2" />
          取消
        </button>
        <button onClick={onSave} className="btn-primary flex items-center whitespace-nowrap" disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          {loading ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  )
}

export default ResearchNotes 